import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

/**
 * Capture a DOM element as JPEG image data
 * @param {HTMLElement} element - Element to capture
 * @param {number} scale - Capture scale (default 1.5)
 * @returns {Promise<string>} - JPEG data URL
 */
const captureElementAsJPEG = async (element, scale = 1.5) => {
  const canvas = await html2canvas(element, {
    scale: scale,
    useCORS: true,
    allowTaint: true,
    backgroundColor: '#ffffff',
    logging: false,
    windowWidth: element?.scrollWidth || 794,
    windowHeight: element?.scrollHeight
  });

  // Convert to JPEG with 0.80 quality for smaller file size
  return canvas?.toDataURL('image/jpeg', 0.80);
};

/**
 * Add image to PDF page with proper scaling to fit A4
 * @param {jsPDF} pdf - PDF document
 * @param {string} imgData - Image data URL
 * @param {number} imgWidth - Original image width
 * @param {number} imgHeight - Original image height
 */
const addImageToPage = (pdf, imgData, imgWidth, imgHeight) => {
  const pageWidth = 210; // A4 width in mm
  const pageHeight = 297; // A4 height in mm
  const margin = 20; // 20mm margins
  const maxWidth = pageWidth - (2 * margin);
  const maxHeight = pageHeight - (2 * margin);

  // Scale image to fit within margins
  let finalWidth = imgWidth;
  let finalHeight = imgHeight;

  if (finalWidth > maxWidth) {
    const ratio = maxWidth / finalWidth;
    finalWidth = maxWidth;
    finalHeight = finalHeight * ratio;
  }

  if (finalHeight > maxHeight) {
    const ratio = maxHeight / finalHeight;
    finalHeight = maxHeight;
    finalWidth = finalWidth * ratio;
  }

  // Center image on page
  const x = (pageWidth - finalWidth) / 2;
  const y = margin;

  pdf?.addImage(imgData, 'JPEG', x, y, finalWidth, finalHeight, undefined, 'FAST');
};

/**
 * Generate PDF report with page-by-page capture to prevent straddling
 * @param {Object} reportConfig - Report configuration
 * @param {Object} summaryMetrics - Summary metrics for report date
 * @param {Array} chartData30 - 30-day chart data
 * @param {Array} chartData90 - 90-day chart data
 * @param {Array} chartDataRange - Selected range chart data
 * @param {Array} sessionHistory - Session history
 * @param {Array} wellnessHistory - Wellness history
 * @returns {Promise<void>}
 */
export const generatePDFReport = async (
  reportConfig,
  summaryMetrics,
  chartData30,
  chartData90,
  chartDataRange,
  sessionHistory,
  wellnessHistory
) => {
  try {
    // Wait for fonts and images to load
    await document.fonts?.ready;
    await new Promise(resolve => setTimeout(resolve, 500));

    // Create PDF with compression enabled
    const pdf = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: 'a4',
      compress: true
    });

    let isFirstPage = true;

    // Helper to add new page
    const addPage = () => {
      if (!isFirstPage) {
        pdf?.addPage();
      }
      isFirstPage = false;
    };

    // PAGE 1: Header + Today's Summary (all three cards)
    const summarySection = document.getElementById('pdf-summary-section');
    if (summarySection) {
      summarySection.style.left = '0';
      summarySection.style.top = '0';
      await new Promise(resolve => setTimeout(resolve, 300));

      const imgData = await captureElementAsJPEG(summarySection, 1.5);
      addPage();
      const imgWidth = 170; // A4 width minus margins
      const imgHeight = (summarySection?.scrollHeight * imgWidth) / summarySection?.scrollWidth;
      addImageToPage(pdf, imgData, imgWidth, imgHeight);

      summarySection.style.left = '-9999px';
    }

    // PAGE 2: Figure 1 (Load + ACWR, 30d)
    const figure1 = document.getElementById('pdf-figure-1');
    if (figure1) {
      figure1.style.left = '0';
      figure1.style.top = '0';
      await new Promise(resolve => setTimeout(resolve, 300));

      const imgData = await captureElementAsJPEG(figure1, 2.0);
      addPage();
      const imgWidth = 170;
      const imgHeight = (figure1?.scrollHeight * imgWidth) / figure1?.scrollWidth;
      addImageToPage(pdf, imgData, imgWidth, imgHeight);

      figure1.style.left = '-9999px';
    }

    // PAGE 3: Figure 2 (Load + ACWR, 90d)
    const figure2 = document.getElementById('pdf-figure-2');
    if (figure2) {
      figure2.style.left = '0';
      figure2.style.top = '0';
      await new Promise(resolve => setTimeout(resolve, 300));

      const imgData = await captureElementAsJPEG(figure2, 2.0);
      addPage();
      const imgWidth = 170;
      const imgHeight = (figure2?.scrollHeight * imgWidth) / figure2?.scrollWidth;
      addImageToPage(pdf, imgData, imgWidth, imgHeight);

      figure2.style.left = '-9999px';
    }

    // PAGE 4: Figure 3 (Load + Wellness, selected range)
    const figure3 = document.getElementById('pdf-figure-3');
    if (figure3) {
      figure3.style.left = '0';
      figure3.style.top = '0';
      await new Promise(resolve => setTimeout(resolve, 300));

      const imgData = await captureElementAsJPEG(figure3, 2.0);
      addPage();
      const imgWidth = 170;
      const imgHeight = (figure3?.scrollHeight * imgWidth) / figure3?.scrollWidth;
      addImageToPage(pdf, imgData, imgWidth, imgHeight);

      figure3.style.left = '-9999px';
    }

    // PAGE 5: Figure 4 (Readiness only, selected range)
    const figure4 = document.getElementById('pdf-figure-4');
    if (figure4) {
      figure4.style.left = '0';
      figure4.style.top = '0';
      await new Promise(resolve => setTimeout(resolve, 300));

      const imgData = await captureElementAsJPEG(figure4, 2.0);
      addPage();
      const imgWidth = 170;
      const imgHeight = (figure4?.scrollHeight * imgWidth) / figure4?.scrollWidth;
      addImageToPage(pdf, imgData, imgWidth, imgHeight);

      figure4.style.left = '-9999px';
    }

    // PAGES 6+: Tables (if included)
    if (reportConfig?.includeSessionHistory || reportConfig?.includeWellnessHistory) {
      const tablesSection = document.getElementById('pdf-tables-section');
      if (tablesSection) {
        tablesSection.style.left = '0';
        tablesSection.style.top = '0';
        await new Promise(resolve => setTimeout(resolve, 300));

        const imgData = await captureElementAsJPEG(tablesSection, 1.5);
        const imgWidth = 170;
        const imgHeight = (tablesSection?.scrollHeight * imgWidth) / tablesSection?.scrollWidth;
        const pageHeight = 257; // A4 height minus margins

        // Tables may span multiple pages
        let heightLeft = imgHeight;
        let position = 0;

        while (heightLeft > 0) {
          addPage();
          pdf?.addImage(imgData, 'JPEG', 20, 20 + position, imgWidth, imgHeight, undefined, 'FAST');
          heightLeft -= pageHeight;
          position -= pageHeight;
        }

        tablesSection.style.left = '-9999px';
      }
    }

    // Add page numbers to all pages
    const pageCount = pdf?.internal?.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      pdf?.setPage(i);
      pdf?.setFontSize(9);
      pdf?.setTextColor(100);
      pdf?.text(
        `Page ${i} of ${pageCount}`,
        pdf?.internal?.pageSize?.getWidth() / 2,
        pdf?.internal?.pageSize?.getHeight() - 10,
        { align: 'center' }
      );
    }

    // Generate filename
    const dateStr = new Date(reportConfig.reportDate)?.toISOString()?.split('T')?.[0];
    const filename = `training-load-report-${dateStr}.pdf`;

    // Download PDF
    pdf?.save(filename);

    return true;
  } catch (error) {
    console.error('Error generating PDF:', error);
    throw new Error(`Failed to generate PDF: ${error.message}`);
  }
};